/**
 * Words for a parked payment event (TMMPDM-0071, TMM-B3). A pay-api event type, a reason code and a
 * resolution each have catalog copy; a value the console doesn't know is never shown as a raw code in a
 * sentence — an unknown event type is the one place a code is shown (mono, in the table), because that
 * is the only thing that identifies it; an unknown reason says only that the event couldn't be applied.
 */
import { PARKED_REASON_CODES } from '@/api/tmm/types';
import type { ErrorTranslate } from '@/api/errors';

/**
 * The pay-api feed event types tmm-api's feed handlers act on (tmm-api `PayApiFeedService` at 174e686).
 * The YAML types `eventType` as a plain string, so a type outside this list renders as its code.
 */
export const PARKED_EVENT_TYPES = [
  'subscription.renewed',
  'subscription.payment_failed',
  'subscription.canceled',
  'subscription.activated',
  'subscription.payment_method_updated',
  'subscription.paused',
  'subscription.resumed',
  'subscription.plan_changed',
  'subscription.renewal_upcoming',
  'payment.succeeded',
  'payment.failed',
  'payment.refunded',
  'connect_account.updated',
  'transfer.created',
  'transfer.failed',
  'transfer.reversed',
  'transfer.reversal_failed',
] as const;

/** `payment.refunded` → `payment_refunded`: a catalog key segment has no dot. */
export function eventTypeKey(type: string): string {
  return type.replace(/\./g, '_');
}

const KNOWN_EVENT_TYPES: ReadonlySet<string> = new Set(PARKED_EVENT_TYPES);
const KNOWN_REASONS: ReadonlySet<string> = new Set(PARKED_REASON_CODES);

/** The event type in words, or `null` for a type the console has no words for (the caller shows the code). */
export function eventTypeWords(type: string, t: ErrorTranslate): string | null {
  if (!KNOWN_EVENT_TYPES.has(type)) return null;
  return t(`parkedEvents.eventType.${eventTypeKey(type)}`, type);
}

/** A code the console doesn't know says so. */
export function reasonWords(code: string, t: ErrorTranslate): string {
  if (!KNOWN_REASONS.has(code))
    return t('parkedEvents.reasonUnknown', "The event couldn't be applied.");
  return t(`parkedEvents.reason.${code}`, code);
}

/**
 * What a person copies for support: the row's identifiers and the handler's exception name. Plain
 * `key: value` lines in English whatever the locale, so support reads one format. `exceptionClass` is
 * only ever copied, never displayed (TMM-B3); an unsent value is written `-`.
 */
export function supportDetails(event: {
  seq: number;
  eventType: string;
  reasonCode: string;
  exceptionClass: string | null;
  paymentId: string | null;
  subscriptionId: string | null;
}): string {
  const value = (v: string | null) => (v === null || v === '' ? '-' : v);
  return [
    `seq: ${event.seq}`,
    `eventType: ${value(event.eventType)}`,
    `reasonCode: ${value(event.reasonCode)}`,
    `exceptionClass: ${value(event.exceptionClass)}`,
    `paymentId: ${value(event.paymentId)}`,
    `subscriptionId: ${value(event.subscriptionId)}`,
  ].join('\n');
}

/**
 * The reason inside "Still refused: …" (`parkedEvents.reapply.stillRefused`): a fragment of that
 * sentence, so it loses its final stop, and in es, fr and pt its first capital (nothing capitalises
 * after a colon there). English keeps the capital the copy was written with.
 */
export function refusalReason(code: string, t: ErrorTranslate, language: string): string {
  const words = reasonWords(code, t).replace(/\.$/, '');
  return language.startsWith('en') ? words : words.charAt(0).toLowerCase() + words.slice(1);
}
